import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Download, Send, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { LetterComposeSheet } from "./LetterComposeSheet";
import { InvitationLetter, JournalistOption, Language } from "./types";

const LANG_FLAGS: Record<Language, string> = { fr: "🇫🇷", en: "🇬🇧", pt: "🇵🇹" };

interface InvitationLettersTabProps {
  orgId: string;
  journalists: JournalistOption[];
  commsEmail: string;
  currentUserName: string;
  currentUserTitle: string;
}

export function InvitationLettersTab({
  orgId,
  journalists,
  commsEmail,
  currentUserName,
  currentUserTitle,
}: InvitationLettersTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [composeOpen, setComposeOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filterLang, setFilterLang] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [resendingId, setResendingId] = useState<string | null>(null);

  const { data: letters = [], isLoading } = useQuery({
    queryKey: ["invitation_letters", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invitation_letters" as any)
        .select(`
          *,
          journalist:journalist_id (first_name, last_name, email)
        `)
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });
      if (error) throw error;

      // Enrich with outlet name from journalists list
      return (data as any[]).map((l) => ({
        ...l,
        outlet_name:
          journalists.find((j) => j.id === l.journalist_id)?.outlet_name ?? null,
      })) as InvitationLetter[];
    },
    enabled: !!orgId,
  });

  async function handleResend(letter: InvitationLetter) {
    setResendingId(letter.id);
    try {
      const { data, error } = await supabase.functions.invoke(
        "send-accreditation-invite",
        {
          body: {
            journalist_id: letter.journalist_id,
            letter_id: letter.id,
            language: letter.language,
          },
        }
      );
      if (error || !data?.success) throw new Error(data?.error ?? error?.message);
      toast({ title: "Email resent successfully" });
      queryClient.invalidateQueries({ queryKey: ["invitation_letters", orgId] });
    } catch (err: any) {
      toast({ title: "Resend failed", description: err.message, variant: "destructive" });
    } finally {
      setResendingId(null);
    }
  }

  const filtered = letters.filter((l) => {
    const journalist = l.journalist as any;
    const name = journalist
      ? `${journalist.first_name} ${journalist.last_name}`
      : "";
    const matchSearch =
      !search ||
      name.toLowerCase().includes(search.toLowerCase()) ||
      l.reference_number.toLowerCase().includes(search.toLowerCase());
    const matchLang = filterLang === "all" || l.language === filterLang;
    const matchStatus =
      filterStatus === "all" ||
      (filterStatus === "dispatched" && !!l.dispatched_at) ||
      (filterStatus === "draft" && !l.dispatched_at);
    return matchSearch && matchLang && matchStatus;
  });

  return (
    <div className="space-y-4">
      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={() => setComposeOpen(true)}
          style={{ backgroundColor: "#006B3C" }}
          className="text-white hover:opacity-90"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Letter
        </Button>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search journalist or reference…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={filterLang} onValueChange={setFilterLang}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Language" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="fr">🇫🇷 FR</SelectItem>
            <SelectItem value="en">🇬🇧 EN</SelectItem>
            <SelectItem value="pt">🇵🇹 PT</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="dispatched">Dispatched</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ backgroundColor: "#0D2240" }}>
              {["Reference", "Journalist", "Language", "Dispatched", "Channel", "Actions"].map(
                (h) => (
                  <TableHead
                    key={h}
                    style={{ color: "#F0E4B8" }}
                    className="font-semibold"
                  >
                    {h}
                  </TableHead>
                )
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                  No letters found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((letter, idx) => {
                const journalist = letter.journalist as any;
                const name = journalist
                  ? `${journalist.first_name} ${journalist.last_name}`
                  : "";
                return (
                  <TableRow
                    key={letter.id}
                    className={idx % 2 === 1 ? "bg-[#F7F5F0]" : ""}
                  >
                    <TableCell>
                      <span className="font-mono text-[#006B3C] text-sm">
                        {letter.reference_number}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{name}</div>
                      {letter.outlet_name && (
                        <div className="text-xs text-muted-foreground">
                          {letter.outlet_name}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="gap-1">
                        {LANG_FLAGS[letter.language as Language]}{" "}
                        {letter.language.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {letter.dispatched_at ? (
                        new Date(letter.dispatched_at).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      ) : (
                        <span className="text-amber-600 text-sm font-medium">
                          Not yet sent
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {letter.dispatch_channel.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {letter.pdf_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(letter.pdf_url!, "_blank")}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={resendingId === letter.id}
                          onClick={() => handleResend(letter)}
                        >
                          {resendingId === letter.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Send className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <LetterComposeSheet
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onDispatched={() =>
          queryClient.invalidateQueries({ queryKey: ["invitation_letters", orgId] })
        }
        journalists={journalists}
        orgId={orgId}
        commsEmail={commsEmail}
        currentUserName={currentUserName}
        currentUserTitle={currentUserTitle}
      />
    </div>
  );
}
